/**
 * TypeScript wrappers for WASM edit mode topology operations.
 * Converts between app types (Set<number>, PolygonTopology)
 * and WASM flat typed arrays.
 */
import { dlog } from "../debug/debugLogger.ts";
import { getWireEdgePairs } from "../modeling/topology.ts";
import type {
  PolygonTopology,
  RenderTriangleIndices,
} from "../modeling/types.ts";
import { asRenderTriangleIndices, EDGE_WIRE } from "../modeling/types.ts";
import type { TopologyResult as WasmTopologyResult } from "./pkg/poseref_wasm.js";
import * as wasmModule from "./pkg/poseref_wasm.js";

// Re-export types that callers need
export type { MergeStrategy } from "../modeling/ops/merge.ts";

// ---------------------------------------------------------------------------
// Conversion helpers
// ---------------------------------------------------------------------------

const EMPTY_U32 = new Uint32Array(0);

function requireWasmExport<T>(name: string): T {
  const value = (wasmModule as Record<string, unknown>)[name];
  if (value == null) {
    throw new Error(
      `WASM binding "${name}" is missing from src/wasm/pkg/poseref_wasm.js. Rebuild the web WASM bundle to refresh generated bindings.`,
    );
  }
  return value as T;
}

const wasmDeleteVertices = requireWasmExport<
  typeof wasmModule.edit_delete_vertices
>("edit_delete_vertices");
const wasmDeleteEdges =
  requireWasmExport<typeof wasmModule.edit_delete_edges>("edit_delete_edges");
const wasmDeleteFaces =
  requireWasmExport<typeof wasmModule.edit_delete_faces>("edit_delete_faces");
const wasmDeleteOnlyEdgesAndFaces = requireWasmExport<
  typeof wasmModule.edit_delete_only_edges_and_faces
>("edit_delete_only_edges_and_faces");
const wasmDeleteOnlyFaces = requireWasmExport<
  typeof wasmModule.edit_delete_only_faces
>("edit_delete_only_faces");
const wasmDissolveVertices = requireWasmExport<
  typeof wasmModule.edit_dissolve_vertices
>("edit_dissolve_vertices");
const wasmDissolveEdges = requireWasmExport<
  typeof wasmModule.edit_dissolve_edges
>("edit_dissolve_edges");
const wasmDissolveFaces = requireWasmExport<
  typeof wasmModule.edit_dissolve_faces
>("edit_dissolve_faces");
const wasmSubdivide =
  requireWasmExport<typeof wasmModule.edit_subdivide>("edit_subdivide");
const wasmExtrude =
  requireWasmExport<typeof wasmModule.edit_extrude>("edit_extrude");
const wasmExtrudeFaces =
  requireWasmExport<typeof wasmModule.edit_extrude_faces>("edit_extrude_faces");
const wasmExtrudeEdges =
  requireWasmExport<typeof wasmModule.edit_extrude_edges>("edit_extrude_edges");
const wasmComputeSelectionNormal = requireWasmExport<
  typeof wasmModule.edit_compute_selection_normal
>("edit_compute_selection_normal");
const wasmInset = requireWasmExport<typeof wasmModule.edit_inset>("edit_inset");
const wasmMergeVertices = requireWasmExport<
  typeof wasmModule.edit_merge_vertices
>("edit_merge_vertices");
const wasmMergeByDistance = requireWasmExport<
  typeof wasmModule.edit_merge_by_distance
>("edit_merge_by_distance");
const wasmBridgeEdgeLoops = requireWasmExport<
  typeof wasmModule.edit_bridge_edge_loops
>("edit_bridge_edge_loops");
const wasmFillFace =
  requireWasmExport<typeof wasmModule.edit_fill_face>("edit_fill_face");
const wasmInsertEdgeLoop = requireWasmExport<
  typeof wasmModule.edit_insert_edge_loop
>("edit_insert_edge_loop");
const wasmRip = requireWasmExport<typeof wasmModule.edit_rip>("edit_rip");
const wasmConnectPath =
  requireWasmExport<typeof wasmModule.edit_connect_path>("edit_connect_path");
const wasmGridFill =
  requireWasmExport<typeof wasmModule.edit_grid_fill>("edit_grid_fill");
const wasmBevel = requireWasmExport<typeof wasmModule.edit_bevel>("edit_bevel");
const wasmSmooth =
  requireWasmExport<typeof wasmModule.edit_smooth>("edit_smooth");

function getWasmExtrudeManifold(): (
  ...args: unknown[]
) => WasmTopologyResult | null {
  return requireWasmExport("edit_extrude_manifold");
}

function getWasmExtrudeIndividualFaces(): (
  ...args: unknown[]
) => WasmTopologyResult | null {
  return requireWasmExport("edit_extrude_individual_faces");
}

function setToSorted(s: Set<number> | ReadonlySet<number>): Uint32Array {
  const arr = new Uint32Array(s.size);
  let i = 0;
  for (const v of s) arr[i++] = v;
  arr.sort();
  return arr;
}

function iterableToArray(values: Iterable<number>): Uint32Array {
  const arr = Array.from(values);
  return Uint32Array.from(arr);
}

/** Convert a set of edge indices into a flat Uint32Array of vertex pairs for WASM. */
function edgeIndicesToPairs(
  edgeIndices: Set<number>,
  topoEdges: Uint32Array,
): Uint32Array {
  const arr = new Uint32Array(edgeIndices.size * 2);
  let i = 0;
  for (const ei of edgeIndices) {
    const a = topoEdges[ei * 2];
    const b = topoEdges[ei * 2 + 1];
    arr[i++] = Math.min(a, b);
    arr[i++] = Math.max(a, b);
  }
  return arr;
}

/** Sentinel value in `VertexProvenance.remap` indicating an old vertex was
 *  deleted or merged away during the op. Mirrors the Rust-side `VERTEX_REMOVED`
 *  in `common/provenance.rs`. */
export const VERTEX_REMOVED = -1;

/**
 * The canonical "where did each vertex come from" record produced by every
 * topology op. Two complementary halves:
 *
 * - `remap[oldIdx]` = the new index this old vertex now occupies, or
 *   `VERTEX_REMOVED` if it was deleted / merged away. Iterate in the OLD
 *   direction to forward-project per-vertex *index* sets (vertex group
 *   memberships).
 *
 * - `parents` (CSR via `parentOffsets`): for each NEW vert `i`, the OLD
 *   vert indices it inherits from are
 *   `parents[parentOffsets[i] .. parentOffsets[i+1]]`. A surviving vert has
 *   one parent (itself), a merge survivor has many, a subdivide midpoint
 *   has two (the edge endpoints), a face center has K. Iterate in the NEW
 *   direction to back-project per-vertex *value* arrays (skin radii, sculpt
 *   displacements, base positions) — average the parents for floats, union
 *   for sets.
 *
 * Both halves are derivable from each other but both are provided: each
 * consumer picks the natural direction for its data. The Rust side already
 * has every op's data internally; this type just exposes it.
 */
export interface VertexProvenance {
  /** length = old vertex count; `remap[old] = new index | VERTEX_REMOVED` */
  remap: Int32Array;
  /** length = new vertex count + 1; CSR offsets into `parents` */
  parentOffsets: Uint32Array;
  /** flat CSR of OLD-vert parent indices for each NEW vert */
  parents: Uint32Array;
}

/** The output shape of every topology op. `provenance` is required — there
 *  is no fallback path; consumers always reconcile per-vertex attributes
 *  through it. */
export interface OpResult {
  vertices: Float32Array;
  indices: RenderTriangleIndices;
  topology: PolygonTopology;
  newSelection: Set<number>;
  newSelectedFaces?: Set<number>;
  faceSources?: Uint8Array;
  faceOwnerVertices?: Uint32Array;
  provenance: VertexProvenance;
}

// `identityProvenance` lives in `modeling/provenance.ts` alongside the rest
// of the JS-side provenance utilities — re-exported from this module's
// barrel (`modeling/meshOps.ts`) so callers continue to import it from one
// place.
import { identityProvenance } from "../modeling/provenance.ts";

export { identityProvenance };

function unpack(result: WasmTopologyResult): OpResult {
  const positions = new Float32Array(result.positions);
  const indices = asRenderTriangleIndices(new Uint32Array(result.indices));
  const offsets = new Uint32Array(result.offsets);
  const corners = new Uint32Array(result.corners);
  const edges = new Uint32Array(result.edges);
  const cornerEdges = new Uint32Array(result.corner_edges);
  const edgeFlags = new Uint8Array(result.edge_flags);
  const newSelection = new Set<number>(
    result.new_selection as Iterable<number>,
  );
  const newSelectedFacesRaw = (
    result as WasmTopologyResult & { new_selected_faces?: Uint32Array }
  ).new_selected_faces;
  const newSelectedFaces =
    newSelectedFacesRaw && newSelectedFacesRaw.length > 0
      ? new Set<number>(newSelectedFacesRaw as Iterable<number>)
      : undefined;
  const faceSourcesRaw = (
    result as WasmTopologyResult & { face_sources?: Uint8Array }
  ).face_sources;
  const faceSources =
    faceSourcesRaw && faceSourcesRaw.length > 0
      ? new Uint8Array(faceSourcesRaw)
      : undefined;
  const faceOwnerVerticesRaw = (
    result as WasmTopologyResult & { face_owner_vertices?: Uint32Array }
  ).face_owner_vertices;
  const faceOwnerVertices =
    faceOwnerVerticesRaw && faceOwnerVerticesRaw.length > 0
      ? new Uint32Array(faceOwnerVerticesRaw)
      : undefined;
  // Provenance crosses the boundary as three flat typed arrays — the same
  // shape every op produces via the Rust `ProvenanceBuilder::finalize` pipe.
  const provenanceRaw = result as WasmTopologyResult & {
    vertex_remap?: Int32Array;
    vertex_parent_offsets?: Uint32Array;
    vertex_parents?: Uint32Array;
  };
  const provenance: VertexProvenance = {
    remap: new Int32Array(provenanceRaw.vertex_remap ?? new Int32Array(0)),
    parentOffsets: new Uint32Array(
      provenanceRaw.vertex_parent_offsets ?? new Uint32Array(0),
    ),
    parents: new Uint32Array(
      provenanceRaw.vertex_parents ?? new Uint32Array(0),
    ),
  };
  result.free();

  if (import.meta.env.DEV) {
    const vertexCount = positions.length / 3;
    if (positions.length % 3 !== 0) {
      throw new Error(
        `WASM unpack: positions length ${positions.length} not divisible by 3`,
      );
    }
    if (offsets.length < 1) {
      throw new Error("WASM unpack: offsets array is empty");
    }
    if (offsets[0] !== 0) {
      throw new Error(`WASM unpack: offsets[0] = ${offsets[0]}, expected 0`);
    }
    const lastOffset = offsets[offsets.length - 1];
    if (lastOffset !== corners.length) {
      throw new Error(
        `WASM unpack: last offset ${lastOffset} !== corners.length ${corners.length}`,
      );
    }
    for (let i = 0; i < corners.length; i++) {
      if (corners[i] >= vertexCount) {
        throw new Error(
          `WASM unpack: corner[${i}] = ${corners[i]} >= vertexCount ${vertexCount}`,
        );
      }
    }
    if (edges.length % 2 !== 0) {
      throw new Error(`WASM unpack: edges length ${edges.length} is odd`);
    }
    if (cornerEdges.length !== corners.length) {
      throw new Error(
        `WASM unpack: cornerEdges length ${cornerEdges.length} !== corners.length ${corners.length}`,
      );
    }
    if (edgeFlags.length !== edges.length / 2) {
      throw new Error(
        `WASM unpack: edgeFlags length ${edgeFlags.length} !== edgeCount ${edges.length / 2}`,
      );
    }
    if (faceSources && faceSources.length !== offsets.length - 1) {
      throw new Error(
        `WASM unpack: faceSources length ${faceSources.length} !== faceCount ${offsets.length - 1}`,
      );
    }
    if (faceOwnerVertices && faceOwnerVertices.length !== offsets.length - 1) {
      throw new Error(
        `WASM unpack: faceOwnerVertices length ${faceOwnerVertices.length} !== faceCount ${offsets.length - 1}`,
      );
    }
    for (const vi of newSelection) {
      if (vi >= vertexCount) {
        throw new Error(
          `WASM unpack: newSelection contains vertex ${vi} >= vertexCount ${vertexCount}`,
        );
      }
    }
  }

  dlog.info(
    "wasm",
    "wasm.unpack",
    `${positions.length / 3}v ${offsets.length - 1}f ${edges.length / 2}e ${newSelection.size}sel`,
    {
      vertexCount: positions.length / 3,
      faceCount: offsets.length - 1,
      cornerCount: corners.length,
      edgeCount: edges.length / 2,
      selectionSize: newSelection.size,
    },
  );

  return {
    vertices: positions,
    indices,
    topology: { offsets, corners, edges, cornerEdges, edgeFlags },
    newSelection,
    newSelectedFaces,
    faceSources,
    faceOwnerVertices,
    provenance,
  };
}

/** Build an `OpResult` that returns the input geometry unchanged. Used as the
 *  no-op result when a WASM op declines to act (empty selection, degenerate
 *  case) — the caller still gets a uniform `OpResult` shape with an identity
 *  provenance, so `applyResult`'s reconciliation path doesn't need a
 *  conditional branch. */
function noOpResult(
  vertices: Float32Array,
  indices: RenderTriangleIndices,
  topology: PolygonTopology,
): OpResult {
  return {
    vertices,
    indices,
    topology,
    newSelection: new Set(),
    provenance: identityProvenance(vertices.length / 3),
  };
}

function topo(t: PolygonTopology) {
  return {
    offsets: t.offsets,
    corners: t.corners,
    loose: getWireEdgePairs(t),
    edges: t.edges,
    cornerEdges: t.cornerEdges,
    edgeFlags: t.edgeFlags,
  };
}

// ---------------------------------------------------------------------------
// Delete operations
// ---------------------------------------------------------------------------

export function deleteVertices(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  toDelete: Set<number>,
): OpResult {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const t0 = performance.now();
  const result = wasmDeleteVertices(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(toDelete),
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.deleteVertices",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return noOpResult(vertices, _indices, topology);
  return unpack(result);
}

export function deleteEdges(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Set<number>,
): OpResult {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const t0 = performance.now();
  const result = wasmDeleteEdges(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selected),
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.deleteEdges",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return noOpResult(vertices, _indices, topology);
  return unpack(result);
}

export function deleteFaces(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Set<number>,
  explicitFaces?: Set<number>,
): OpResult {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const t0 = performance.now();
  const result = wasmDeleteFaces(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selected),
    explicitFaces ? setToSorted(explicitFaces) : EMPTY_U32,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.deleteFaces",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return noOpResult(vertices, _indices, topology);
  return unpack(result);
}

export function deleteOnlyEdgesAndFaces(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Set<number>,
): OpResult {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const t0 = performance.now();
  const result = wasmDeleteOnlyEdgesAndFaces(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selected),
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.deleteOnlyEdgesAndFaces",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return noOpResult(vertices, _indices, topology);
  return unpack(result);
}

export function deleteOnlyFaces(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Set<number>,
  explicitFaces?: Set<number>,
): OpResult {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const t0 = performance.now();
  const result = wasmDeleteOnlyFaces(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selected),
    explicitFaces ? setToSorted(explicitFaces) : EMPTY_U32,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.deleteOnlyFaces",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return noOpResult(vertices, _indices, topology);
  return unpack(result);
}

export function dissolveVertices(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Set<number>,
): OpResult {
  const { offsets, corners, loose, cornerEdges, edges, edgeFlags } =
    topo(topology);
  const t0 = performance.now();
  const result = wasmDissolveVertices(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selected),
    cornerEdges,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.dissolveVertices",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return noOpResult(vertices, _indices, topology);
  return unpack(result);
}

export function dissolveEdges(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Set<number>,
): OpResult {
  const { offsets, corners, loose, cornerEdges, edges, edgeFlags } =
    topo(topology);
  const t0 = performance.now();
  const result = wasmDissolveEdges(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selected),
    cornerEdges,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.dissolveEdges",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return noOpResult(vertices, _indices, topology);
  return unpack(result);
}

export function dissolveFaces(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Set<number>,
  explicitFaces?: Set<number>,
): OpResult {
  const { offsets, corners, loose, cornerEdges, edges, edgeFlags } =
    topo(topology);
  const t0 = performance.now();
  const result = wasmDissolveFaces(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selected),
    explicitFaces ? setToSorted(explicitFaces) : EMPTY_U32,
    cornerEdges,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.dissolveFaces",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return noOpResult(vertices, _indices, topology);
  return unpack(result);
}

// ---------------------------------------------------------------------------
// Topology operations
// ---------------------------------------------------------------------------

export function subdivide(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Set<number>,
) {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const t0 = performance.now();
  const result = wasmSubdivide(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selected),
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.subdivide",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  return unpack(result);
}

export function extrudeVertices(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Set<number>,
) {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const t0 = performance.now();
  const result = wasmExtrude(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selected),
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.extrudeVertices",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  return unpack(result);
}

export function extrudeFaces(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selectedFaces: Set<number>,
) {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const faceArr = setToSorted(selectedFaces);
  const t0 = performance.now();
  const result = wasmExtrudeFaces(
    vertices,
    offsets,
    corners,
    loose,
    faceArr,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.extrudeFaces",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  const out = unpack(result);
  return {
    ...out,
    newSelectedFaces: out.newSelectedFaces ?? new Set(selectedFaces),
  };
}

export function extrudeIndividualFaces(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selectedFaces: Set<number>,
) {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const faceArr = setToSorted(selectedFaces);
  const t0 = performance.now();
  const result = getWasmExtrudeIndividualFaces()(
    vertices,
    offsets,
    corners,
    loose,
    faceArr,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.extrudeIndividualFaces",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  const out = unpack(result);
  return {
    ...out,
    newSelectedFaces: out.newSelectedFaces ?? new Set(selectedFaces),
  };
}

export function extrudeManifold(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selectedFaces: Set<number>,
  direction: [number, number, number],
  distance: number,
) {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const faceArr = setToSorted(selectedFaces);
  const t0 = performance.now();
  const result = getWasmExtrudeManifold()(
    vertices,
    offsets,
    corners,
    loose,
    faceArr,
    direction[0],
    direction[1],
    direction[2],
    distance,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.extrudeManifold",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  const out = unpack(result);
  return {
    ...out,
    newSelectedFaces: out.newSelectedFaces ?? new Set(selectedFaces),
  };
}

export function extrudeEdges(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selectedEdges: Set<number>,
) {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const edgeArr = edgeIndicesToPairs(selectedEdges, topology.edges);
  const t0 = performance.now();
  const result = wasmExtrudeEdges(
    vertices,
    offsets,
    corners,
    loose,
    edgeArr,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.extrudeEdges",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  return unpack(result);
}

export function computeSelectionNormal(
  vertices: Float32Array,
  normals: Float32Array,
  topology: PolygonTopology,
  selected: Set<number>,
): [number, number, number] {
  // Wire-only meshes (e.g. Skin Armature) have no face topology and therefore
  // no per-corner normals.  Compute the average tangent of the wire edges
  // incident to the selected vertices — the result orients the gizmo along the
  // strand so that E (extrude) naturally extends the wire.
  if (topology.offsets.length <= 1) {
    return computeWireTangent(vertices, topology, selected);
  }
  const t0 = performance.now();
  const result = wasmComputeSelectionNormal(
    vertices,
    normals,
    topology.offsets,
    topology.corners,
    setToSorted(selected),
  );
  dlog.info(
    "wasm",
    "wasm.computeSelectionNormal",
    `done (${(performance.now() - t0).toFixed(1)}ms)`,
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      normal: [result[0], result[1], result[2]],
    },
  );
  return [result[0], result[1], result[2]];
}

/**
 * Compute the average wire tangent for the given selected vertices.
 *
 * For each wire edge incident to a selected vertex:
 *   - If both endpoints are selected (interior edge), add the signed edge
 *     direction (towards the unselected neighbor, or arbitrarily for
 *     fully-selected edges).
 *   - If one endpoint is unselected (boundary edge), point outward from the
 *     selected endpoint toward the unselected one (the natural extrusion direction).
 *
 * The accumulated vector is normalized to produce the final tangent.
 */
function computeWireTangent(
  vertices: Float32Array,
  topology: PolygonTopology,
  selected: Set<number>,
): [number, number, number] {
  const { edges, edgeFlags } = topology;
  const edgeCount = edges.length / 2;
  let tx = 0,
    ty = 0,
    tz = 0;

  for (let i = 0; i < edgeCount; i++) {
    if (!(edgeFlags[i] & EDGE_WIRE)) continue;
    const a = edges[i * 2],
      b = edges[i * 2 + 1];
    const aSelected = selected.has(a);
    const bSelected = selected.has(b);
    if (!aSelected && !bSelected) continue;

    // Orient the tangent: from the selected vertex toward its neighbor, or
    // from a toward b for interior (both-selected) edges.
    const [v0, v1] = bSelected && !aSelected ? [b, a] : [a, b];
    const dx = vertices[v1 * 3] - vertices[v0 * 3];
    const dy = vertices[v1 * 3 + 1] - vertices[v0 * 3 + 1];
    const dz = vertices[v1 * 3 + 2] - vertices[v0 * 3 + 2];
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (len > 1e-10) {
      tx += dx / len;
      ty += dy / len;
      tz += dz / len;
    }
  }

  const mag = Math.sqrt(tx * tx + ty * ty + tz * tz);
  return mag > 1e-10 ? [tx / mag, ty / mag, tz / mag] : [0, 1, 0];
}

export function insetFaces(
  vertices: Float32Array,
  topology: PolygonTopology,
  selectedFaces: Set<number>,
  amount: number,
  depth: number = 0,
  individual: boolean = false,
  evenOffset: boolean = true,
  markSharp: boolean = false,
  markSeam: boolean = false,
) {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const t0 = performance.now();
  const result = (
    wasmInset as unknown as (
      positions: Float32Array,
      offsets: Uint32Array,
      corners: Uint32Array,
      loose_edges: Uint32Array,
      selected_faces: Uint32Array,
      amount: number,
      depth: number,
      individual: boolean,
      even_offset: boolean,
      mark_sharp: boolean,
      mark_seam: boolean,
      old_edges: Uint32Array,
      old_edge_flags: Uint8Array,
    ) => WasmTopologyResult | undefined
  )(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selectedFaces),
    amount,
    depth,
    individual,
    evenOffset,
    markSharp,
    markSeam,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.insetFaces",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  return unpack(result);
}

export function mergeVertices(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Set<number>,
  strategy: "center" | "first" | "last",
) {
  const strategyNum = strategy === "center" ? 0 : strategy === "first" ? 1 : 2;
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const t0 = performance.now();
  const result = wasmMergeVertices(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selected),
    strategyNum,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.mergeVertices",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  return unpack(result);
}

export function mergeByDistance(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Set<number>,
  threshold: number,
) {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const t0 = performance.now();
  const result = wasmMergeByDistance(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selected),
    threshold,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.mergeByDistance",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  return unpack(result);
}

export function bridgeEdgeLoops(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selectedEdges: Set<number>,
  twist: number = 0,
  reverse: boolean = false,
  seedEdge: [number, number] | null = null,
) {
  const { offsets, corners, loose, cornerEdges, edges, edgeFlags } =
    topo(topology);
  const t0 = performance.now();
  const result = wasmBridgeEdgeLoops(
    vertices,
    offsets,
    corners,
    loose,
    edgeIndicesToPairs(selectedEdges, topology.edges),
    twist,
    reverse,
    seedEdge?.[0] ?? -1,
    seedEdge?.[1] ?? -1,
    cornerEdges,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.bridgeEdgeLoops",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  return unpack(result);
}

export function fillFace(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Set<number>,
): OpResult | null {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const t0 = performance.now();
  const result = wasmFillFace(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selected),
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.fillFace",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  return unpack(result);
}

export function insertEdgeLoop(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  cutEdgeV0: number,
  cutEdgeV1: number,
  t: number = 0.5,
  cuts: number = 1,
) {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const t0 = performance.now();
  const result = wasmInsertEdgeLoop(
    vertices,
    offsets,
    corners,
    loose,
    cutEdgeV0,
    cutEdgeV1,
    t,
    cuts,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.insertEdgeLoop",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  return unpack(result);
}

export function ripVertices(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: ReadonlySet<number>,
  mouseWorldX: number,
  mouseWorldY: number,
  mouseWorldZ: number,
): OpResult | null {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const t0 = performance.now();
  const result = wasmRip(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selected),
    mouseWorldX,
    mouseWorldY,
    mouseWorldZ,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.ripVertices",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  return unpack(result);
}

export function connectVertexPath(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Iterable<number>,
  activeVertex: number | null = null,
): OpResult | null {
  const { offsets, corners, loose, cornerEdges, edges, edgeFlags } =
    topo(topology);
  const t0 = performance.now();
  const result = wasmConnectPath(
    vertices,
    offsets,
    corners,
    loose,
    iterableToArray(selected),
    activeVertex ?? -1,
    cornerEdges,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.connectVertexPath",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  return unpack(result);
}

export function gridFill(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: ReadonlySet<number>,
  span: number = 1,
  offset: number = 0,
  seedVertex: number | null = null,
): OpResult | null {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const t0 = performance.now();
  const result = wasmGridFill(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selected),
    span,
    offset,
    seedVertex ?? -1,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.gridFill",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  return unpack(result);
}

export function bevelGeometry(
  vertices: Float32Array,
  topology: PolygonTopology,
  selectedFaces: Set<number>,
  selectedEdges: Set<number>,
  amount: number,
  segments: number = 1,
  profile: number = 0.5,
  clampOverlap: boolean = true,
  markSharp: boolean = true,
  markSeam: boolean = false,
) {
  const { offsets, corners, loose, cornerEdges, edges, edgeFlags } =
    topo(topology);
  const t0 = performance.now();
  const result = wasmBevel(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selectedFaces),
    edgeIndicesToPairs(selectedEdges, topology.edges),
    amount,
    segments,
    profile,
    clampOverlap,
    markSharp,
    markSeam,
    cornerEdges,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.bevelGeometry",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
    },
  );
  if (!result) return null;
  return unpack(result);
}

/**
 * Uniform Laplacian smoothing ("Smooth Vertices" / Relax).
 *
 * Only `selectedVertices` move; unselected vertices stay put but still
 * contribute to each selected vertex's 1-ring neighbour average. Topology
 * is unchanged — `OpResult.topology` mirrors the input and provenance is
 * identity, so every per-vertex attribute survives untouched.
 *
 * `axisMask` bits: 1=X, 2=Y, 4=Z. All on = no per-axis lock.
 */
export function smoothSelection(
  vertices: Float32Array,
  _indices: RenderTriangleIndices,
  topology: PolygonTopology,
  selectedVertices: Set<number>,
  iterations: number,
  factor: number,
  axisMask: number,
): OpResult | null {
  const { offsets, corners, loose, edges, edgeFlags } = topo(topology);
  const t0 = performance.now();
  const result = wasmSmooth(
    vertices,
    offsets,
    corners,
    loose,
    setToSorted(selectedVertices),
    iterations >>> 0,
    factor,
    axisMask >>> 0,
    edges,
    edgeFlags,
  );
  dlog.info(
    "wasm",
    "wasm.smoothSelection",
    result ? `done (${(performance.now() - t0).toFixed(1)}ms)` : "no-op",
    {
      durationMs: +(performance.now() - t0).toFixed(1),
      hasResult: !!result,
      selectionSize: selectedVertices.size,
      iterations,
      factor,
      axisMask,
    },
  );
  if (!result) return null;
  return unpack(result);
}

// Re-export functions that stay in JS (no WASM equivalent needed)
export { edgeExists } from "../modeling/ops/bridge.ts";
export { previewEdgeLoop } from "../modeling/ops/edgeLoop.ts";
export { concatMeshes } from "../modeling/ops/merge.ts";
